import { onboardingModalModel, useOnboarding } from '@edu-frontend/modules/src/shared/ui/OnboardingModal';
import { getLocalStorage } from '@edu-frontend/modules/src/shared/lib/localStorage';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getDoodleSettings } from 'platform-components/src/components/app-factory/selectors';
import { useRouteMatch } from 'react-router';
import { getExperimentFlags, useStaticUrls } from 'platform-components';
import { getClasses } from 'common.components/user/selectors';

export const DOODLE_NAMESPACE = 'doodle';

const { getFromLocalStorage, setInLocalStorage } =
    getLocalStorage<Record<`doodle_${string}`, boolean>>(DOODLE_NAMESPACE);

export const openDoodle = () => {
    onboardingModalModel.open({ id: DOODLE_NAMESPACE });
};

const getDoodleId = (doodleId = '') => {
    return `doodle_${doodleId}` as const;
};

const getDoodleFromLocalStorage = (doodleId = '') => {
    const doodleLocalStorageKey = getDoodleId(doodleId);
    return getFromLocalStorage(doodleLocalStorageKey);
};

const setDoddleInLocalStorage = (doodleId = '', value = true) => {
    const doodleLocalStorageKey = getDoodleId(doodleId);
    setInLocalStorage(doodleLocalStorageKey, value);
};

export const useDoodle = () => {
    const doodleSettings = useSelector(getDoodleSettings);
    const experimentsFlags = useSelector(getExperimentFlags) as Partial<Record<string, number>> | null;
    const teams = useSelector(getClasses);
    const isDoodleShowed = getDoodleFromLocalStorage(doodleSettings?.id);
    const staticUrls = useStaticUrls();
    const isShowcaseUrl = useRouteMatch([staticUrls.labLibraryMain, staticUrls.labLibraryTeamMain]);

    // eslint-disable-next-line complexity
    const { slides, serviceLogo, serviceLogoText, serviceLogoShort, handleDoodleClick, doodleEnabled } = useMemo(() => {
        const currentDate = Date.now();
        const isAvailableDateEnd =
            !doodleSettings?.endDate || currentDate < new Date(doodleSettings?.endDate).getTime();
        const isAvailableDateStart =
            !doodleSettings?.startDate || currentDate > new Date(doodleSettings?.startDate).getTime();
        const isDoodleModalActive = !!doodleSettings?.header || !!doodleSettings?.img || !!doodleSettings?.description;
        const isDoodleAvailable = doodleSettings?.enabled && isAvailableDateStart && isAvailableDateEnd;
        const onClick = () => {
            if (isDoodleAvailable && isShowcaseUrl) {
                openDoodle();
            }
        };
        return {
            isDoodleAvailable: doodleSettings?.enabled && isAvailableDateStart && isAvailableDateEnd,
            slides: isDoodleModalActive
                ? [
                      {
                          id: '1',
                          header: doodleSettings.header,
                          img: doodleSettings.img,
                          description: doodleSettings.description,
                      },
                  ]
                : [],
            handleDoodleClick: onClick,
            serviceLogo: isDoodleAvailable ? doodleSettings?.serviceLogo : undefined,
            serviceLogoShort: isDoodleAvailable ? doodleSettings?.serviceLogoShort : undefined,
            serviceLogoText: isDoodleAvailable ? doodleSettings?.serviceLogoText : undefined,
            doodleEnabled: isDoodleAvailable,
        };
    }, [
        doodleSettings?.description,
        doodleSettings?.enabled,
        doodleSettings?.endDate,
        doodleSettings?.header,
        doodleSettings?.img,
        doodleSettings?.serviceLogo,
        doodleSettings?.serviceLogoShort,
        doodleSettings?.serviceLogoText,
        doodleSettings?.startDate,
        isShowcaseUrl,
    ]);

    useEffect(() => {
        // открываем doodle, если он показывается в первый раз
        if (doodleEnabled && !isDoodleShowed && teams.length !== 0 && !!experimentsFlags?.enableDoodleModal) {
            openDoodle();
            setDoddleInLocalStorage(doodleSettings?.id);
        }
    }, [doodleEnabled, doodleSettings?.id, experimentsFlags?.enableDoodleModal, isDoodleShowed, teams.length]);

    useOnboarding({ id: DOODLE_NAMESPACE, slides });

    return { handleDoodleClick, serviceLogo, serviceLogoText, serviceLogoShort };
};
